import { useCallback, useState } from "react"
import { mergeAttributes, Node } from "@tiptap/core"
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react"

import { cn } from "@productlane/lib/src/cn"
import { Button } from "@productlane/ui"

import type { NodeViewProps } from "@tiptap/react"
import { readImageSize } from "@/lib/image-size"
import { selectAdjacentImage } from "./image-adjacent-selection"
import { stopMediaNodeViewEvent } from "./media-drag"
import { useMirroredLinearImage } from "./use-mirrored-linear-image"

function ImageNodeView({
	node,
	updateAttributes,
	editor,
	getPos,
	selected,
}: NodeViewProps) {
	const [isHovered, setIsHovered] = useState(false)
	const [isLoading, setIsLoading] = useState(false)

	const isInTable = (() => {
		try {
			const pos = getPos()
			if (pos == null) return false
			const $pos = editor.state.doc.resolve(pos)
			for (let depth = $pos.depth; depth > 0; depth--) {
				if ($pos.node(depth).type.name === "tableCell") return true
				if ($pos.node(depth).type.name === "tableHeader") return true
			}
			return false
		} catch {
			return false
		}
	})()

	const currentSrc = node.attrs.src as string
	const { src: displaySrc, isPending } = useMirroredLinearImage(currentSrc)
	const processedSrc = node.attrs["data-processed-src"] as string | undefined
	const originalSrc = node.attrs["data-original-src"] as string | undefined

	const hasBackgroundOptions = processedSrc && originalSrc
	const isShowingOriginal = currentSrc === originalSrc

	const toggleBackground = useCallback(() => {
		if (!processedSrc || !originalSrc) return

		setIsLoading(true)

		const newSrc = isShowingOriginal ? processedSrc : originalSrc
		const minimumDelay = new Promise((resolve) => setTimeout(resolve, 1000))
		// The plate and the original differ in size, so the stored box follows the swap.
		void Promise.all([readImageSize(newSrc), minimumDelay]).then(([size]) => {
			updateAttributes({
				src: newSrc,
				width: size?.width ?? null,
				height: size?.height ?? null,
			})
			setIsLoading(false)
		})
	}, [isShowingOriginal, processedSrc, originalSrc, updateAttributes])

	return (
		<NodeViewWrapper
			className="relative my-[2.375rem] block w-fit"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{isPending ? (
				<span className="block h-40 w-64 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
			) : (
				<img
					data-drag-preview
					src={displaySrc}
					width={(node.attrs.width as number | string | null) ?? undefined}
					height={(node.attrs.height as number | string | null) ?? undefined}
					// Carries the original through a copy-paste: parseHTML reads it back
					// so a mirrored URL never lands in the stored description.
					data-linear-src={displaySrc === currentSrc ? undefined : currentSrc}
					alt={(node.attrs.alt as string | null) ?? ""}
					className={cn(
						"h-auto max-w-full rounded-lg",
						isLoading && "opacity-70",
					)}
					draggable={false}
				/>
			)}
			{selected && (
				// Drawn inside the image box, so a narrow container never clips it.
				<span
					data-image-selection-ring
					className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-inset ring-green-500"
				/>
			)}
			{hasBackgroundOptions && (isHovered || isLoading) && (
				<div
					className={cn(
						"absolute",
						isInTable ? "right-1 top-1" : "right-2 top-2",
					)}
				>
					<Button
						variant="glass"
						size={isInTable ? "sm" : "lg"}
						// A focused button inside the node view swallows Cmd+Z.
						onMouseDown={(event) => event.preventDefault()}
						onClick={toggleBackground}
						disabled={isLoading}
					>
						{isLoading
							? "Processing..."
							: isShowingOriginal
								? "Add background"
								: "Remove background"}
					</Button>
				</div>
			)}
		</NodeViewWrapper>
	)
}

export const CustomImageExtension = Node.create({
	name: "image",

	addOptions() {
		return {
			inline: false,
			allowBase64: false,
			HTMLAttributes: {},
		}
	},

	inline() {
		return (this.options as { inline: boolean }).inline
	},

	group() {
		return (this.options as { inline: boolean }).inline ? "inline" : "block"
	},

	draggable: true,

	addAttributes() {
		return {
			src: {
				default: null,
				parseHTML: (element) =>
					element.getAttribute("data-linear-src") ??
					element.getAttribute("src"),
				renderHTML: (attributes: Record<string, string>) => {
					if (!attributes.src) {
						return {}
					}
					return { src: attributes.src }
				},
			},
			alt: {
				default: null,
				parseHTML: (element) => element.getAttribute("alt"),
				renderHTML: (attributes: Record<string, string>) => {
					if (!attributes.alt) {
						return {}
					}
					return { alt: attributes.alt }
				},
			},
			width: { default: null },
			height: { default: null },
			title: {
				default: null,
				parseHTML: (element) => element.getAttribute("title"),
				renderHTML: (attributes: Record<string, string>) => {
					if (!attributes.title) {
						return {}
					}
					return { title: attributes.title }
				},
			},
			"data-processed-src": {
				default: null,
				parseHTML: (element) => element.getAttribute("data-processed-src"),
				renderHTML: (attributes: Record<string, string>) => {
					if (!attributes["data-processed-src"]) {
						return {}
					}
					return { "data-processed-src": attributes["data-processed-src"] }
				},
			},
			"data-original-src": {
				default: null,
				parseHTML: (element) => element.getAttribute("data-original-src"),
				renderHTML: (attributes: Record<string, string>) => {
					if (!attributes["data-original-src"]) {
						return {}
					}
					return { "data-original-src": attributes["data-original-src"] }
				},
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: (this.options as { allowBase64: boolean }).allowBase64
					? "img[src]"
					: 'img[src]:not([src^="data:"])',
			},
		]
	},

	renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, string> }) {
		return [
			"img",
			mergeAttributes(
				(this.options as { HTMLAttributes: Record<string, string> })
					.HTMLAttributes,
				HTMLAttributes,
			),
		]
	},

	addNodeView() {
		return ReactNodeViewRenderer(ImageNodeView, {
			stopEvent: ({ event }) =>
				stopMediaNodeViewEvent(event, this.editor.isEditable),
		})
	},

	addKeyboardShortcuts() {
		const selectImageFirst = (direction: -1 | 1) => () => {
			const tr = selectAdjacentImage(this.editor.state, direction, this.name)
			if (!tr) return false
			this.editor.view.dispatch(tr)
			return true
		}

		return {
			Backspace: selectImageFirst(-1),
			Delete: selectImageFirst(1),
		}
	},

	addCommands() {
		return {
			setImage:
				(options) =>
				({ commands }) => {
					return commands.insertContent({
						type: this.name,
						attrs: options,
					})
				},
		}
	},
})
